/**
 * The plan's category — the backend's `TrainingPlanCategory`, and this app's key for it.
 *
 * Six values, parsed by NAME and never by ordinal (`EnumName.cs`): `Strength`, `Cardio`, `Hiit`,
 * `Flexibility`, `Bodyweight`, `Hybrid`. This module is the one place that list is written, because
 * three surfaces now depend on it agreeing with itself — the library's chip, the library's filter
 * pills, and the create modal's picker — and a fourth (the cover pool) is keyed on it.
 *
 * ── Why the key is the lower-cased enum, and why it is a CLOSED set ──────────────────────────
 * `plans.category.<lowercase>` is the key scheme the upstream locale files already use and the same
 * normalisation the mobile app performs, so reading them is exactly what a verbatim copy is for.
 * The set is closed rather than derived by lower-casing whatever arrived, because `t()` returns the
 * key itself on a miss: a category this build has never heard of would otherwise render in the
 * table as `plans.category.tabata`, looking like copy somebody wrote.
 *
 * `null` is therefore a real answer — "the wire sent a category this build does not know" — and
 * every consumer has to decide what to do with it. The library shows the server's own spelling; the
 * cover resolver falls back to a neutral pool.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. The LABEL is a display concern and
 * belongs to whichever surface renders it; the COLOUR likewise (`shared/ui/planCategoryColour.ts`).
 */

/** The six, as this app keys them. */
export type PlanCategoryKey =
  | 'strength'
  | 'cardio'
  | 'hiit'
  | 'flexibility'
  | 'bodyweight'
  | 'hybrid';

/**
 * The six in the canon's own left-to-right order — the order the library's filter pills sit in and
 * the order the create modal's picker wraps in. Declared once so the two cannot drift, and it is
 * NOT alphabetical in either language: the canon leads with the two commonest categories.
 */
export const PLAN_CATEGORY_ORDER: readonly PlanCategoryKey[] = [
  'strength',
  'cardio',
  'hiit',
  'flexibility',
  'bodyweight',
  'hybrid',
];

/** The wire spelling each key round-trips as. Exhaustive over the union by construction. */
const TO_WIRE: Record<PlanCategoryKey, string> = {
  strength: 'Strength',
  cardio: 'Cardio',
  hiit: 'Hiit',
  flexibility: 'Flexibility',
  bodyweight: 'Bodyweight',
  hybrid: 'Hybrid',
};

/**
 * The wire's `TrainingPlanCategory` → this app's key, or `null` when this build does not know it.
 *
 * Tolerant about casing and whitespace, closed about membership. `Object.hasOwn` before indexing,
 * for the reason `executionModeFromApi` gives: a bare index walks the prototype chain, so
 * `"constructor"` off the wire would yield a function rather than a miss.
 */
export function planCategoryKey(value: string | null | undefined): PlanCategoryKey | null {
  if (value == null) return null;
  const key = value.trim().toLowerCase();
  return (PLAN_CATEGORY_ORDER as readonly string[]).includes(key)
    ? (key as PlanCategoryKey)
    : null;
}

/** This app's key → the wire spelling. The inverse, in the same file, so the pair is reviewable. */
export function planCategoryToApi(key: PlanCategoryKey): string {
  return TO_WIRE[key];
}
